import { percentageToDecimal, parseFormNumber } from '@/utils/formatters';
import type { AccountingFramework, LpAllocationMode } from '@/types/fund';
import type { FundFormData } from './types';

type FundType = 'main' | 'feeder' | 'master' | 'aiv' | 'opportunity';
type LegalStructure = 'scs' | 'scsp' | 'sarl' | 'sicav' | 'limited_partnership';
type FundStatus =
  'setup' | 'fundraising' | 'investing' | 'harvesting' | 'liquidated';
type RegulatoryRegime = 'aifmd' | 'ucits' | 'eltif';

/**
 * Transformed fund data ready for API calls
 */
interface TransformedFundData {
  name: string;
  display_name?: string;
  fund_type: FundType;
  legal_structure: LegalStructure;
  domicile_country: string;
  base_currency: string;
  minimum_prepayment_pool?: number;
  target_size?: number;
  hard_cap?: number;
  first_closing_date?: string;
  final_closing_date?: string;
  fund_term_years?: number;
  extension_periods?: number;
  vintage_year: number;
  status: FundStatus;
  lei_code?: string;
  tax_id?: string;
  coc_number: string | null;
  regulatory_regime?: RegulatoryRegime;
  accounting_framework: AccountingFramework;
  lp_allocation_mode: LpAllocationMode;
  parent_fund_id?: string;
  master_fund_id?: string;
}

/**
 * Transform complete form data to Fund API format (for create/update)
 */
export function transformFormToFundData(
  formData: FundFormData
): TransformedFundData {
  return {
    name: formData.name,
    display_name: formData.display_name || undefined,
    fund_type: formData.fund_type as FundType,
    legal_structure: formData.legal_structure as LegalStructure,
    domicile_country: formData.domicile_country,
    base_currency: formData.base_currency,
    minimum_prepayment_pool: formData.minimum_prepayment_pool ?? undefined,
    target_size: formData.target_size ?? undefined,
    hard_cap: formData.hard_cap ?? undefined,
    first_closing_date: formData.first_closing_date || undefined,
    final_closing_date: formData.final_closing_date || undefined,
    fund_term_years: parseFormNumber(formData.fund_term_years),
    extension_periods: parseFormNumber(formData.extension_periods),
    vintage_year:
      parseFormNumber(formData.vintage_year) ?? new Date().getFullYear(),
    status: formData.status as FundStatus,
    lei_code: formData.lei_code || undefined,
    tax_id: formData.tax_id || undefined,
    // Null, not undefined, so a cleared field clears the stored value.
    coc_number: formData.coc_number.trim() || null,
    regulatory_regime: formData.regulatory_regime as
      RegulatoryRegime | undefined,
    accounting_framework: formData.accounting_framework as AccountingFramework,
    lp_allocation_mode: formData.lp_allocation_mode as LpAllocationMode,
    parent_fund_id:
      formData.parent_fund_id && formData.parent_fund_id !== 'none'
        ? formData.parent_fund_id
        : undefined,
    master_fund_id:
      formData.master_fund_id && formData.master_fund_id !== 'none'
        ? formData.master_fund_id
        : undefined,
  };
}

/**
 * Transform LPA terms fields from form data to API format
 */
export function transformFormToLPATermsData(formData: FundFormData) {
  const investmentPeriodYears = parseFormNumber(
    formData.investment_period_years
  );

  return {
    investment_period_fee_rate: formData.management_fee_investment_period
      ? percentageToDecimal(formData.management_fee_investment_period)
      : 0.02, // Default 2%
    investment_period_fee_basis_id:
      parseFormNumber(formData.management_fee_basis_investment) ?? 1, // Default to first option
    post_investment_period_fee_rate: formData.management_fee_post_investment
      ? percentageToDecimal(formData.management_fee_post_investment)
      : 0.015, // Default 1.5%
    post_investment_period_fee_basis_id:
      parseFormNumber(formData.management_fee_basis_post_investment) ?? 1, // Default to first option
    investment_period_duration: investmentPeriodYears
      ? investmentPeriodYears * 12 // Convert years to months
      : 60, // Default 5 years = 60 months
    gp_commitment: formData.gp_commitment
      ? percentageToDecimal(formData.gp_commitment)
      : 0.01, // Default 1%
    hurdle_rate: formData.hurdle_rate
      ? percentageToDecimal(formData.hurdle_rate)
      : 0.08, // Default 8%
    carried_interest: formData.carried_interest
      ? percentageToDecimal(formData.carried_interest)
      : 0.2, // Default 20%
    catch_up_rate: formData.gp_catch_up
      ? percentageToDecimal(formData.gp_catch_up)
      : 1.0, // Default 100%
    distribution_waterfall_type_id:
      parseFormNumber(formData.waterfall_type) ?? 1, // Default to first option
    fund_costs_cap: formData.fund_costs_cap
      ? percentageToDecimal(formData.fund_costs_cap)
      : undefined,
  };
}
